import { MawpCalcType } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import {
  WORKING_UNIT_PRESSURE,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import isNil from 'lodash/isNil'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

// - - - - - - - - - - - - - - - - - Pressure Vessel Cylindrical Shell Inner Pressure  - - - - - - - - - - - - - - - - -
// - - - - - - - - - - - - - - - - -                  API 579 2C.3                     - - - - - - - - - - - - - - - - -
export type ComputePVCylindricalShellMawpProps = {
  t: GeckoMeasurement | null // effective thickness
  tsl: GeckoMeasurement | null // longitudinal stress
  tNom: GeckoMeasurement | null // nominal thickness
  yieldStrength: GeckoMeasurement | null // S
  qualityFactor: GeckoMeasurement | null // E
  outerDiameter: GeckoMeasurement | null // D
  // need to add whether corrosion is inner or outer
  axis: 'x' | 'y'
  calcType: MawpCalcType | null // Case a and Case b in API 579 2C.3
}

// Calculation Types for circumference:
// Case A, thin wall: P <= 0.385 * S * E and t^c_min <= 0.5 * R
// Case B, thick wall: P > 0.385 * S * E OR t^c_min > 0.5 * R

// Calculation Types for longitudinal:
// Case C, thin wall: P <= 1.25 * S * E and t^L_min <= 0.5 * R
// Case D, thick wall: P > 1.25 * S * E OR t^L_min > 0.5 * R

// Cylindrical Shell Inner Pressure MAWP Calculation - API 579 2C.3.3
export function computePVCylindricalShellMAWP({
  t,
  tsl,
  tNom,
  yieldStrength,
  qualityFactor,
  outerDiameter,
  axis,
  calcType,
}: ComputePVCylindricalShellMawpProps): GeckoMeasurement | null {
  if (
    isNil(t) ||
    isNil(yieldStrength) ||
    isNil(qualityFactor) ||
    isNil(outerDiameter) ||
    isNil(tNom) ||
    isNil(calcType)
  ) {
    return null
  }

  const tNum = t.toNumber(WORKING_UNIT_THICKNESS)
  const sNum = yieldStrength.toNumber(WORKING_UNIT_PRESSURE)
  const eNum = qualityFactor.toNumber(GeckoMeasurementUnit.Ratio)
  const dNum = outerDiameter.toNumber(WORKING_UNIT_THICKNESS)
  const tNomNum = tNom.toNumber(WORKING_UNIT_THICKNESS)
  const innerRadiusNum = dNum / 2 - tNomNum

  if (axis === 'x') {
    // Cylindrical Shell Inner Pressure Circumferential MAWP

    const mawpNum =
      calcType === 'thinWall'
        ? (sNum * eNum * tNum) / (innerRadiusNum + 0.6 * tNum)
        : sNum * eNum * Math.log(innerRadiusNum + tNum / innerRadiusNum)

    return GeckoMeasurement.CreateFromValue(mawpNum, WORKING_UNIT_PRESSURE)
  } else {
    // Cylindrical Shell Inner Pressure Longitudinal MAWP
    if (isNil(tsl)) return null
    const tslNum = tsl.toNumber(WORKING_UNIT_THICKNESS)

    const mawpNum =
      calcType === 'thinWall'
        ? (2 * sNum * eNum * (tNum - tslNum)) /
          (innerRadiusNum - 0.4 * (tNum - tslNum))
        : sNum *
          eNum *
          (Math.pow((innerRadiusNum + (tNum - tslNum)) / innerRadiusNum, 2) - 1)

    return GeckoMeasurement.CreateFromValue(mawpNum, WORKING_UNIT_PRESSURE)
  }
}

export type ComputePVCylindricalShellThicknessFromMawpProps = {
  mawp: GeckoMeasurement | null // MAWP
  tsl: GeckoMeasurement | null // longitudinal stress
  tNom: GeckoMeasurement | null // nominal thickness
  yieldStrength: GeckoMeasurement | null // S
  qualityFactor: GeckoMeasurement | null // E
  outerDiameter: GeckoMeasurement | null // D
  axis: 'x' | 'y'
  calcType: 'thinWall' | 'thickWall' // Case a and Case b in API 579 2C.3
}

// Inverse function of computePVCylindricalShellMAWP
export function computePVCylindricalShellThicknessFromMAWP({
  mawp,
  tsl,
  tNom,
  yieldStrength,
  qualityFactor,
  outerDiameter,
  axis,
  calcType,
}: ComputePVCylindricalShellThicknessFromMawpProps): GeckoMeasurement | null {
  if (
    isNil(mawp) ||
    isNil(yieldStrength) ||
    isNil(qualityFactor) ||
    isNil(outerDiameter) ||
    isNil(tNom)
  ) {
    return null
  }

  const mawpNum = mawp.toNumber(WORKING_UNIT_PRESSURE)
  const sNum = yieldStrength.toNumber(WORKING_UNIT_PRESSURE)
  const eNum = qualityFactor.toNumber(GeckoMeasurementUnit.Ratio)
  const dNum = outerDiameter.toNumber(WORKING_UNIT_THICKNESS)
  const tNomNum = tNom.toNumber(WORKING_UNIT_THICKNESS)
  const innerRadiusNum = dNum / 2 - tNomNum

  if (axis === 'x') {
    const tNum =
      calcType === 'thinWall'
        ? (mawpNum * innerRadiusNum) / (sNum * eNum - 0.6 * mawpNum)
        : innerRadiusNum * (Math.exp(mawpNum / (sNum * eNum)) - 1)
    return GeckoMeasurement.CreateFromValue(tNum, WORKING_UNIT_THICKNESS)
  } else {
    if (isNil(tsl)) return null
    const tslNum = tsl.toNumber(WORKING_UNIT_THICKNESS)
    const tNum =
      calcType === 'thinWall'
        ? (mawpNum * innerRadiusNum) / (2 * sNum * eNum + 0.4 * mawpNum) +
          tslNum
        : innerRadiusNum * (Math.pow(mawpNum / (sNum * eNum) + 1, 0.5) - 1) +
          tslNum

    return GeckoMeasurement.CreateFromValue(tNum, WORKING_UNIT_THICKNESS)
  }
}
